export const CONTENT_GOVERNANCE_SCHEMA_VERSION = "forfeo-lot4h-h1-v1" as const;

export const CONTENT_DOMAINS = {
  categories: {
    label: "Catégories",
    operationalOwner: "EXPLORE",
    financialAuthority: "GIFTSHOP",
    requiresBilingualPublication: true,
  },
  regions: {
    label: "Régions",
    operationalOwner: "EXPLORE",
    financialAuthority: "GIFTSHOP",
    requiresBilingualPublication: true,
  },
  partners: {
    label: "Partenaires",
    operationalOwner: "EXPLORE",
    financialAuthority: "GIFTSHOP",
    requiresBilingualPublication: true,
  },
  articles: {
    label: "Articles et blogue",
    operationalOwner: "EXPLORE",
    financialAuthority: "GIFTSHOP",
    requiresBilingualPublication: true,
  },
  editorialRules: {
    label: "Règles éditoriales",
    operationalOwner: "EXPLORE",
    financialAuthority: "GIFTSHOP",
    requiresBilingualPublication: true,
  },
  moderationQueue: {
    label: "File de modération",
    operationalOwner: "EXPLORE",
    financialAuthority: "GIFTSHOP",
    requiresBilingualPublication: false,
  },
} as const;

export type ContentDomain = keyof typeof CONTENT_DOMAINS;

export const CONTENT_STAGES = [
  "draft",
  "submitted",
  "in_review",
  "changes_requested",
  "approved",
  "scheduled",
  "published",
  "archived",
  "rejected",
] as const;

export type ContentStage = (typeof CONTENT_STAGES)[number];

export const CONTENT_TRANSITIONS: Readonly<Record<ContentStage, readonly ContentStage[]>> = {
  draft: ["submitted", "archived"],
  submitted: ["in_review", "draft", "rejected"],
  in_review: ["changes_requested", "approved", "rejected"],
  changes_requested: ["draft", "submitted", "archived"],
  approved: ["scheduled", "published", "changes_requested", "archived"],
  scheduled: ["published", "approved", "archived"],
  published: ["archived", "changes_requested"],
  archived: ["draft"],
  rejected: ["draft", "archived"],
};

export const CONTENT_GOVERNANCE_ROLES = {
  author: {
    permission: "content:author",
    actions: ["create_draft", "edit_draft", "submit", "withdraw"] as const,
  },
  reviewer: {
    permission: "content:reviewer",
    actions: ["start_review", "request_changes", "reject", "recommend_approval"] as const,
  },
  publisher: {
    permission: "content:publisher",
    actions: ["approve", "schedule", "publish", "archive"] as const,
  },
  administrator: {
    permission: "platform:admin",
    actions: ["configure_taxonomy", "assign_roles", "audit_workflow"] as const,
  },
} as const;

export const CONTENT_PUBLICATION_POLICY = {
  serverEnforced: true,
  browserDirectPublicationAllowed: false,
  browserFinancialMutationAllowed: false,
  serviceRoleAllowedInBrowser: false,
  bilingualLocales: ["fr", "en"] as const,
  requireReviewer: true,
  requirePublisher: true,
  separationOfDuties: true,
  requireAuditTrail: true,
  requirePublishedAt: true,
  allowClientProvidedFinancialAuthority: false,
} as const;

export type BilingualEditorialFields = {
  title: { fr: string; en: string };
  summary: { fr: string; en: string };
  body: { fr: string; en: string };
};

export type PublicationCandidate = {
  stage: ContentStage;
  editorial: BilingualEditorialFields;
  reviewerId: string | null;
  publisherId: string | null;
  auditReference: string | null;
};

const present = (value: string | null | undefined) => Boolean(value?.trim());

export function evaluatePublicationReadiness(candidate: PublicationCandidate) {
  const missingLocales = CONTENT_PUBLICATION_POLICY.bilingualLocales.filter((locale) => (
    !present(candidate.editorial.title[locale])
    || !present(candidate.editorial.summary[locale])
    || !present(candidate.editorial.body[locale])
  ));
  const approvedStage = candidate.stage === "approved" || candidate.stage === "scheduled";
  const separated = present(candidate.reviewerId)
    && present(candidate.publisherId)
    && candidate.reviewerId !== candidate.publisherId;

  return {
    ready: approvedStage
      && missingLocales.length === 0
      && Boolean(separated)
      && present(candidate.auditReference),
    approvedStage,
    missingLocales,
    separationOfDuties: Boolean(separated),
    auditTrailPresent: present(candidate.auditReference),
  } as const;
}
